import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {ActionOrigin, DATA_FRAMES_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {DataFrameHeaderMenuOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import DataFrameTableHeaderCell from 'data-frames/table/header/DataFrameTableHeaderCell';
import {isExcludedAccessor, rowIdAccessor} from 'data-frames/util/createAccessors';
import isSystemColumn from 'data-frames/util/isSystemColumn';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK, GRAY_LIGHTER} from 'ui-library/Colors';
import useOnMount from 'ui-library/hooks/useOnMount';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import ResultsPager, {resultsPagerStyles} from 'ui-library/ResultsPager';
import {Cell, CellRenderOptions, Column, simpleTableStyles, Table} from 'ui-library/simple-table/SimpleTable';
import {UseSimpleTableSortStateOutput} from 'ui-library/simple-table/useSimpleTableSortState';
import {JSONValue} from 'util/Types';

import {SystemColumnName} from '../DataFrameConstants';
import {DataFrame, DataFrameColumnDef} from '../DataFrameTypes';
import DataFrameTableCellValue, {MODE_TO_SHOULD_RENDER_CHIPS} from './DataFrameTableCellValue';
import {DataFrameTableMode} from './DataFrameTableConstants';
import DataFrameTableIndexCellContents from './DataFrameTableIndexCellContents';
import useDataFrameTablePaginationState from './hooks/useDataFrameTablePaginationState';
import {UseDataFrameTableSelectionOutput} from './hooks/useDataFrameTableSelectionState';

// Use an object to allow mocking this value in tests.
export const DATA_FRAME_TABLE_CONSTANTS = {
  // Set to 100 to accommodate all wells of a 96W plate on a single page while limiting the number of rows
  // that need to be rendered simultaneously.
  MAX_ROWS_PER_PAGE: 100,
  dataFrameTableContentsRender: (): void => {},
};

export type DataFrameTableSelectionOptions =
  | {allowRowSelection: false}
  | ({allowRowSelection: true} & UseDataFrameTableSelectionOutput);

export interface DataFrameRenderOverrides {
  columnNameToHeaderCellRenderFn?: Record<string, (column: DataFrameColumnDef) => JSX.Element>;
  columnNameToDataCellRenderFn?: Record<
    string,
    (cellValue: JSONValue, row: Record<string, JSONValue>) => JSX.Element
  >;
  /** Renders additional decoration content alongside the default header content for a column. */
  columnNameToHeaderDecorationRenderFn?: Record<string, (column: DataFrameColumnDef) => JSX.Element>;
  /** Renders additional decoration content alongside the default data cell content for a column. */
  columnNameToDataCellDecorationRenderFn?: Record<
    string,
    (cellValue: JSONValue, row: Record<string, JSONValue>) => JSX.Element
  >;
  hiddenColumnNames?: Set<string>;
}
export interface DataFrameTableProps {
  /** IDs are optional, though should be specified when available for analytic event tracking purposes. */
  dataFrameId?: string;
  actionOrigin: ActionOrigin;
  dataFrame: DataFrame;
  clientStateId: string;
  mode: DataFrameTableMode;
  renderIsExcludedColumn?: boolean;
  selectionOptions: DataFrameTableSelectionOptions;
  sortOptions: UseSimpleTableSortStateOutput;
  headerMenuOptions?: DataFrameHeaderMenuOptions;
  dataFrameRenderOverrides?: DataFrameRenderOverrides;
}

function DataFrameTableContents({
  dataFrameId,
  actionOrigin,
  clientStateId,
  dataFrame: {rows, columns, hydratedProperties},
  mode,
  selectionOptions,
  rowsOffset,
  sortOptions: sortStateOptions,
  onToggleSort,
  renderIsExcludedColumn = true,
  headerMenuOptions,
  dataFrameRenderOverrides = {},
}: DataFrameTableProps & {rowsOffset: number; onToggleSort: () => void}): JSX.Element {
  // We call a noop function in the render function of the component to be able to stub it. We can't directly
  // stub the functional component because it's memo'd, so instead we can stub a function that is called on
  // render. Using this noop gives us a consistent function to do that with. This is useful to test if a
  // render happened or not with certain actions (to make sure we're not breaking the React.memo)
  //
  // See "does not frivolously re-render the contents when the table is scrolled" in
  // client/data-frames/table/__tests__/DataFrameTable-test.tsx for usage.
  DATA_FRAME_TABLE_CONSTANTS.dataFrameTableContentsRender();

  const numRowsOnPage = Math.min(DATA_FRAME_TABLE_CONSTANTS.MAX_ROWS_PER_PAGE, rows.length - rowsOffset);

  const minIndexColumnWidth = `${(rowsOffset + numRowsOnPage).toString().length}ch`;

  function pageRowIndexToGlobalRowIndex(pageRowIndex: number): number {
    return pageRowIndex + rowsOffset;
  }

  function globalRowIndexToRowId(globalRowIndex: number): string {
    return rowIdAccessor(rows[globalRowIndex]);
  }

  function handleToggleSelectAll(): void {
    if (selectionOptions.allowRowSelection) {
      selectionOptions.onClickSelectAll(
        pageRowIndexToGlobalRowIndex(0),
        pageRowIndexToGlobalRowIndex(numRowsOnPage - 1)
      );
    }
  }

  function handleToggleSelectRow(pageRowIndex: number, isShiftDown: boolean): void {
    const globalRowIndex = pageRowIndexToGlobalRowIndex(pageRowIndex);
    if (selectionOptions.allowRowSelection) {
      selectionOptions.onClickRow(globalRowIndex, isShiftDown);
    }
  }

  function rowIsExcluded(pageRowIndex: number): boolean {
    const globalRowIndex = pageRowIndexToGlobalRowIndex(pageRowIndex);
    return isExcludedAccessor(rows[globalRowIndex]);
  }

  /**
   * Filter and rearrange DataFrame columns for rendering.
   * Removes system columns, columns in hiddenColumnNames, and appends
   * benchcol_is_excluded as the last column if renderIsExcludedColumn is true.
   */
  function filterAndRearrangeColumns(columns: DataFrame['columns']): DataFrame['columns'] {
    const visibleColumns = columns.filter(
      (column) =>
        !isSystemColumn(column) && !dataFrameRenderOverrides.hiddenColumnNames?.has(column.systemName)
    );

    return renderIsExcludedColumn
      ? visibleColumns.concat(columns.filter((column) => column.systemName === SystemColumnName.IS_EXCLUDED))
      : visibleColumns;
  }

  return (
    <Table
      // Only need to calculate in-view when rendering chips (RICH mode)
      calculateInView={MODE_TO_SHOULD_RENDER_CHIPS[mode]}
      className={css(dataFrameTableStyles.table)}
      emptyTableText="No rows"
      fitContent={true}
      frozenHeader={true}
      getRowKey={(rowIndex: number) => globalRowIndexToRowId(pageRowIndexToGlobalRowIndex(rowIndex))}
      isCompact={true}
      numRows={numRowsOnPage}
      overflowVisible={true}
      showBorder={false}
    >
      <Column
        header={
          <Cell
            className={classNames(
              css(dataFrameTableStyles.cell, simpleTableStyles.grayHoverHeaderCell),
              'simpleTable-checkboxCell'
            )}
            onClick={() => {
              handleToggleSelectAll();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            {selectionOptions.allowRowSelection && (
              <div style={{minWidth: minIndexColumnWidth}}>
                <IndeterminateCheckbox
                  className={css(dataFrameTableStyles.indeterminateCheckbox)}
                  items={rows.map((_, rowIndex) => globalRowIndexToRowId(rowIndex))}
                  selectedMap={(rowId) => selectionOptions.selectedRowIds.has(rowId)}
                  onChange={noop}
                />
              </div>
            )}
          </Cell>
        }
        renderCell={(pageRowIndex: number, {isRowHovered}: CellRenderOptions) => (
          <Cell
            className={css(
              dataFrameTableStyles.cell,
              rowIsExcluded(pageRowIndex) && dataFrameTableStyles.excludedCellWrapper
            )}
            onClick={(e) => {
              handleToggleSelectRow(pageRowIndex, e.shiftKey);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
            }}
          >
            <DataFrameTableIndexCellContents
              index={pageRowIndexToGlobalRowIndex(pageRowIndex) + 1}
              minWidth={minIndexColumnWidth}
              showCheckbox={
                selectionOptions.allowRowSelection &&
                (isRowHovered ||
                  selectionOptions.selectedRowIds.has(
                    globalRowIndexToRowId(pageRowIndexToGlobalRowIndex(pageRowIndex))
                  ))
              }
              isCheckboxSelected={
                selectionOptions.allowRowSelection &&
                selectionOptions.selectedRowIds.has(
                  globalRowIndexToRowId(pageRowIndexToGlobalRowIndex(pageRowIndex))
                )
              }
            />
          </Cell>
        )}
      />
      {filterAndRearrangeColumns(columns).map((column) => (
        <Column
          key={column.systemName}
          header={
            <DataFrameTableHeaderCell
              clientStateId={`${clientStateId}-${dataFrameId}-${column.systemName}`}
              // TODO(BNCH-73213): Disable filtering based on the pipeline's state
              column={column}
              onToggleSort={(sortDirection) => {
                trackEvent(DATA_FRAMES_ANALYTICS_ACTIONS.SORTED_DATA_FRAME_TABLE, {
                  data_frame_id: dataFrameId,
                  action_origin: actionOrigin,
                  column_type: column.type.leafType,
                  previous_sort_direction: sortStateOptions.sortState?.sortDirection ?? null,
                });
                sortStateOptions.onToggleSort(column.systemName, sortDirection);
                onToggleSort();
              }}
              sortDirection={
                sortStateOptions.sortState?.columnKey === column.systemName
                  ? sortStateOptions.sortState.sortDirection
                  : null
              }
              headerMenuOptions={headerMenuOptions}
              columnNameToHeaderCellRenderFn={dataFrameRenderOverrides?.columnNameToHeaderCellRenderFn}
              columnNameToHeaderDecorationRenderFn={
                dataFrameRenderOverrides?.columnNameToHeaderDecorationRenderFn
              }
            />
          }
          renderCell={(pageRowIndex: number, options: CellRenderOptions) => (
            <Cell
              className={css(
                dataFrameTableStyles.cell,
                rowIsExcluded(pageRowIndex) && dataFrameTableStyles.excludedCellWrapper
              )}
            >
              {dataFrameRenderOverrides?.columnNameToDataCellRenderFn?.hasOwnProperty(column.systemName) ? (
                dataFrameRenderOverrides?.columnNameToDataCellRenderFn[column.systemName](
                  rows[pageRowIndexToGlobalRowIndex(pageRowIndex)][column.systemName],
                  rows[pageRowIndexToGlobalRowIndex(pageRowIndex)]
                )
              ) : (
                <DataFrameTableCellValue
                  cellValue={rows[pageRowIndexToGlobalRowIndex(pageRowIndex)][column.systemName]}
                  column={column}
                  hydratedProperties={hydratedProperties}
                  tableMode={mode}
                  // If cell has not been displayed in the viewport, delay rendering chips.
                  delayChipRendering={!(options.hasRowBeenInView ?? true)}
                />
              )}
              {dataFrameRenderOverrides?.columnNameToDataCellDecorationRenderFn?.[column.systemName]?.(
                rows[pageRowIndexToGlobalRowIndex(pageRowIndex)][column.systemName],
                rows[pageRowIndexToGlobalRowIndex(pageRowIndex)]
              )}
            </Cell>
          )}
        />
      ))}
    </Table>
  );
}
const MemoizedDataFrameTableContents = React.memo(DataFrameTableContents);

export default function DataFrameTable(props: DataFrameTableProps): JSX.Element {
  const {rowsOffset, onChangeRowsOffset} = useDataFrameTablePaginationState(
    props.clientStateId,
    props.dataFrame
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // distanceFromBottom represents how many px are left to be scrolled in the table.
  // It is 0 when the user has scrolled to the bottom of the table, or there is no y-overflow.
  const [distanceFromBottom, setDistanceFromBottom] = useState(0);

  const updateDistanceFromBottom = useCallback(() => {
    if (tableWrapperRef.current) {
      const scrollableDistance = tableWrapperRef.current.scrollHeight - tableWrapperRef.current.clientHeight;
      setDistanceFromBottom(scrollableDistance - tableWrapperRef.current.scrollTop);
    }
  }, []);

  const resetScroll = useCallback(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = 0;
      updateDistanceFromBottom();
    }
  }, [updateDistanceFromBottom]);

  useOnMount(() => {
    updateDistanceFromBottom();
  });

  const handleToggleSort = useCallback(() => {
    onChangeRowsOffset(0);
    resetScroll();
  }, [resetScroll, onChangeRowsOffset]);

  return (
    <div
      className={classNames(
        'mediocre-item',
        'printable-container-scaleForPrint',
        css(dataFrameTableStyles.wrapper)
      )}
      {...getTestProps({component: 'DataFrameTable', element: 'root'})}
    >
      <div
        className={classNames('scrollbar-override', css(dataFrameTableStyles.tableWrapper))}
        ref={tableWrapperRef}
        onScroll={updateDistanceFromBottom}
        {...getTestProps({component: 'DataFrameTable', element: 'tableWrapper'})}
      >
        <MemoizedDataFrameTableContents {...props} rowsOffset={rowsOffset} onToggleSort={handleToggleSort} />
      </div>
      <div
        className={classNames('u-vertical-center-items', css(dataFrameTableStyles.bottomPane))}
        // Gradually render less of the shadow as the user gets closer to the bottom of
        // the table, with a max shadow size of 3px
        style={{
          boxShadow: `0 ${-Math.min(distanceFromBottom, 3)}px ${
            Math.min(distanceFromBottom, 3) + 3
          }px 0 rgba(162, 185, 190, 0.3)`,
        }}
      >
        {props.dataFrame.rows.length > DATA_FRAME_TABLE_CONSTANTS.MAX_ROWS_PER_PAGE ? (
          <ResultsPager
            allowFastForwardAndBackward={false}
            isLoading={false}
            setOffset={(newRowsOffset) => {
              trackEvent(DATA_FRAMES_ANALYTICS_ACTIONS.PAGINATED_DATA_FRAME_TABLE, {
                data_frame_id: props.dataFrameId,
                action_origin: props.actionOrigin,
                old_rows_offset: rowsOffset,
                new_rows_offset: newRowsOffset,
              });
              onChangeRowsOffset(newRowsOffset);
              resetScroll();
            }}
            pagination={{
              limit: DATA_FRAME_TABLE_CONSTANTS.MAX_ROWS_PER_PAGE,
              total: props.dataFrame.rows.length,
              offset: rowsOffset,
            }}
            resultDescription="row"
          />
        ) : (
          <span className={classNames(css(resultsPagerStyles.pageDetails))}>
            {`${props.dataFrame.rows.length} ${pluralize(props.dataFrame.rows.length, 'row')}`}
          </span>
        )}
      </div>
    </div>
  );
}

export const dataFrameTableStyles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  tableWrapper: {
    overflowY: 'auto',
  },
  table: {
    borderRadius: 0,
    margin: 0,
  },
  bottomPane: {
    backgroundColor: GRAY_LIGHTER,
    // paddingLeft should be kept in sync with styles.compactCell in ui-library/simple-table/SimpleTable.tsx
    paddingLeft: 8,
    flexBasis: 30,
    flexShrink: 0,
  },
  excludedCellWrapper: {
    backgroundColor: GRAY_LIGHTER,
    color: GRAY_DARK,
  },
  cell: {
    ...FONT_PROXIMA_13,
    height: 36,
  },
  indeterminateCheckbox: {
    transform: 'translateY(2px)',
    width: 16,
    height: 16,
  },
});
